/**
 * OperatorProfitTab — Operator-only profit & cashier-payout-due view.
 *
 * Money flow: cashier physically collects cash from players. At end of day/
 * week they hand cash to the operator. Default split: 80% to operator, 20%
 * cashier keep (set per cashier).
 *
 * Sections:
 *  1. Today + This Week summary cards (loaded / cashed / net profit / op share due)
 *  2. Per-cashier live ledger: today net, week net, currently owed
 *     (since last `cashier_payout`), [Mark Paid] action.
 *
 * Hidden from cashiers (dashboard tab + API 403).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { RefreshCw, Loader2, TrendingUp, TrendingDown, HandCoins, Check, History, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const pct = (n) => `${((Number(n) || 0) * 100).toFixed(0)}%`;

const OperatorProfitTab = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(null); // cashier row being paid
    const [historyFor, setHistoryFor] = useState(null); // cashier row whose history is open

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/operator/profit-commission`, { withCredentials: true });
            setData(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return (
            <div className="text-center py-12 text-zinc-500" data-testid="profit-loading">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading profit report…
            </div>
        );
    }
    if (!data) return null;

    const owed = data.currently_owed || { total: 0, by_cashier: [] };

    return (
        <div className="space-y-5" data-testid="operator-profit-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-base font-semibold text-zinc-100">Profit & cashier payouts</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">
                        Profit = loaded − cashed − free play. Cashiers collect player cash; default split is{' '}
                        <span className="text-amber-300 font-semibold">80% to operator</span> /{' '}
                        <span className="text-cyan-300 font-semibold">20% cashier keep</span>. Adjust per cashier from the Cashiers tab.
                    </p>
                </div>
                <Button onClick={load} size="sm" variant="outline"
                    className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                    data-testid="profit-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            {/* Big "Currently Owed" banner */}
            <div className="rounded-md border border-amber-500/30 bg-gradient-to-br from-amber-950/40 to-zinc-900/40 p-5"
                data-testid="currently-owed-banner">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-amber-200/80 font-semibold">
                            Cashiers currently owe you
                        </p>
                        <p className="text-3xl font-mono font-bold text-amber-300 mt-1"
                            data-testid="currently-owed-total">
                            {fmt(owed.total)}
                        </p>
                    </div>
                    <HandCoins className="w-10 h-10 text-amber-400/40" />
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <PeriodCard label="Today" period={data.today} testKey="today" />
                <PeriodCard label={`This Week · ${data.week_label || ''}`} period={data.this_week} testKey="week" />
            </div>

            {/* Per-cashier ledger */}
            <div className="rounded-md border border-zinc-800 bg-zinc-900/30"
                data-testid="cashier-ledger">
                <div className="px-5 py-3 border-b border-zinc-800">
                    <h4 className="text-sm font-semibold text-zinc-100">Per-cashier ledger</h4>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                        "Currently owed" = unpaid balance since the last Mark Paid. Click Mark Paid when the cashier hands you the cash.
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Cashier</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Split</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Loaded (since paid)</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Cashed (since paid)</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Net</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Owed to you</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Last paid</TableHead>
                                <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {owed.by_cashier.length === 0 && (
                                <TableRow className="border-zinc-800">
                                    <TableCell colSpan={8} className="text-center text-zinc-500 text-sm py-6">
                                        No cashiers on your roster yet.
                                    </TableCell>
                                </TableRow>
                            )}
                            {owed.by_cashier.map((r, i) => (
                                <TableRow key={r.cashier_id} className="border-zinc-800 hover:bg-zinc-900/50"
                                    data-testid={`ledger-row-${i}`}>
                                    <TableCell className="text-zinc-100 font-medium">{r.cashier_name}</TableCell>
                                    <TableCell className="text-right font-mono text-xs">
                                        <span className="text-amber-300">{pct(r.operator_share_pct)}</span>
                                        <span className="text-zinc-600 mx-1">/</span>
                                        <span className="text-cyan-300">{pct(r.cashier_keep_pct)}</span>
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-emerald-300">{fmt(r.loaded)}</TableCell>
                                    <TableCell className="text-right font-mono text-rose-300">{fmt(r.cashed)}</TableCell>
                                    <TableCell className={`text-right font-mono ${r.net >= 0 ? 'text-zinc-100' : 'text-rose-300'}`}>
                                        {fmt(r.net)}
                                    </TableCell>
                                    <TableCell className="text-right font-mono font-semibold text-amber-300"
                                        data-testid={`ledger-owed-${i}`}>
                                        {fmt(r.operator_share_due)}
                                    </TableCell>
                                    <TableCell className="text-xs text-zinc-500">
                                        {r.last_payout_at ? new Date(r.last_payout_at).toLocaleString() : <span className="text-zinc-600">never</span>}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center justify-end gap-1.5">
                                            <Button size="sm" variant="outline"
                                                onClick={() => setHistoryFor(r)}
                                                className="h-7 border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 text-xs"
                                                data-testid={`history-btn-${i}`}>
                                                <History className="w-3 h-3 mr-1" /> History
                                            </Button>
                                            <Button size="sm"
                                                disabled={r.operator_share_due <= 0}
                                                onClick={() => setPaying(r)}
                                                className="h-7 bg-amber-500 hover:bg-amber-400 text-zinc-900 text-xs"
                                                data-testid={`mark-paid-btn-${i}`}>
                                                <Check className="w-3 h-3 mr-1" /> Mark Paid
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Mark Paid dialog */}
            <MarkPaidDialog
                cashier={paying}
                onClose={() => setPaying(null)}
                onPaid={async () => { setPaying(null); await load(); }}
            />

            {/* Payout history dialog */}
            <PayoutHistoryDialog
                cashier={historyFor}
                onClose={() => setHistoryFor(null)}
            />
        </div>
    );
};

const PeriodCard = ({ label, period, testKey }) => {
    const profit = Number(period?.profit || 0);
    const isPos = profit >= 0;
    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/30 p-5 space-y-3"
            data-testid={`profit-period-${testKey}`}>
            <div className="flex items-center justify-between">
                <h4 className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">{label}</h4>
                <span className={`text-[11px] uppercase tracking-wider font-medium px-2 py-0.5 rounded
                    ${isPos ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20'
                            : 'bg-rose-500/10 text-rose-300 border border-rose-500/20'}`}>
                    {isPos ? <TrendingUp className="w-3 h-3 inline mr-1" /> : <TrendingDown className="w-3 h-3 inline mr-1" />}
                    {isPos ? 'profit' : 'loss'}
                </span>
            </div>

            <div className="flex items-baseline gap-2">
                <span className={`text-3xl font-mono font-bold ${isPos ? 'text-emerald-300' : 'text-rose-300'}`}
                    data-testid={`profit-${testKey}-amount`}>
                    {isPos ? '+' : ''}{fmt(profit)}
                </span>
                <span className="text-xs text-zinc-500">net house profit</span>
            </div>

            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-zinc-800">
                <Stat label="Loaded in" value={fmt(period?.loaded)} tone="emerald" />
                <Stat label="Redeemed" value={`-${fmt(period?.cashed)}`} tone="rose" />
                <Stat label="Free play" value={`-${fmt(period?.free_play)}`} tone="purple" />
            </div>

            <div className="pt-2 border-t border-zinc-800 grid grid-cols-2 gap-3">
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">Operator share generated</p>
                    <p className="font-mono text-sm font-semibold text-amber-300"
                        data-testid={`op-share-${testKey}`}>
                        {fmt(period?.total_operator_share_due)}
                    </p>
                </div>
                <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">Cashier keep generated</p>
                    <p className="font-mono text-sm font-semibold text-cyan-300"
                        data-testid={`cashier-keep-${testKey}`}>
                        {fmt(period?.total_cashier_keep)}
                    </p>
                </div>
            </div>
        </div>
    );
};

const Stat = ({ label, value, tone }) => {
    const toneCls = {
        emerald: 'text-emerald-300',
        rose:    'text-rose-300',
        purple:  'text-purple-300',
    }[tone] || 'text-zinc-300';
    return (
        <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
            <p className={`text-sm font-mono ${toneCls}`}>{value}</p>
        </div>
    );
};

const MarkPaidDialog = ({ cashier, onClose, onPaid }) => {
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (cashier) {
            setAmount(String(cashier.operator_share_due || 0));
            setNote('');
        }
    }, [cashier]);

    if (!cashier) return null;

    const submit = async () => {
        const amt = Number(amount);
        if (!Number.isFinite(amt) || amt < 0) {
            toast.error('Enter a valid amount');
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/operator/cashiers/${cashier.cashier_id}/mark-paid`,
                { amount: amt, note: note.trim() || null }, { withCredentials: true });
            toast.success(`Recorded ${fmt(amt)} paid by ${cashier.cashier_name}`);
            await onPaid();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to record payout');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!cashier} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="bg-[#121212] border-white/10" data-testid="mark-paid-dialog">
                <DialogHeader>
                    <DialogTitle className="text-white">Mark paid — {cashier.cashier_name}</DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        Confirm the amount the cashier handed over. This zeros the "currently owed"
                        counter and starts a fresh accrual from now.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div>
                        <Label className="text-white text-xs">Amount received ($)</Label>
                        <Input type="number" min="0" step="0.01"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono text-lg"
                            data-testid="mark-paid-amount" />
                        <p className="text-[11px] text-zinc-500 mt-1">
                            Currently owed: <span className="text-amber-300 font-mono">{fmt(cashier.operator_share_due)}</span>
                        </p>
                    </div>
                    <div>
                        <Label className="text-white text-xs">Note (optional)</Label>
                        <Input value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder="e.g. Friday weekly settle"
                            className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                            data-testid="mark-paid-note" />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}
                        className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800">
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={busy}
                        className="bg-amber-500 hover:bg-amber-400 text-zinc-900"
                        data-testid="mark-paid-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                        Confirm payout
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default OperatorProfitTab;

const PayoutHistoryDialog = ({ cashier, onClose }) => {
    const [list, setList] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [trail, setTrail] = useState(null);
    const [trailLoading, setTrailLoading] = useState(false);

    useEffect(() => {
        if (!cashier) { setList([]); setTrail(null); return; }
        let cancelled = false;
        (async () => {
            setListLoading(true);
            try {
                const { data } = await axios.get(
                    `${API}/operator/cashiers/${cashier.cashier_id}/payouts`,
                    { withCredentials: true });
                if (!cancelled) setList(data?.payouts || []);
            } catch (err) {
                if (!cancelled) toast.error(err.response?.data?.detail || 'Failed to load history');
            } finally {
                if (!cancelled) setListLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [cashier]);

    const openTrail = async (payoutId) => {
        setTrail(null);
        setTrailLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/operator/cashiers/${cashier.cashier_id}/payouts/${payoutId}/trail`,
                { withCredentials: true });
            setTrail(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load trail');
        } finally {
            setTrailLoading(false);
        }
    };

    if (!cashier) return null;

    return (
        <Dialog open={!!cashier} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="bg-[#121212] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto"
                data-testid="payout-history-dialog">
                <DialogHeader>
                    <DialogTitle className="text-white">
                        Payout history — {cashier.cashier_name}
                    </DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        {trail
                            ? 'Click a tile again to switch payouts, or close the trail to return.'
                            : 'Click any settle to see the customer activity it covered.'}
                    </DialogDescription>
                </DialogHeader>

                {!trail && (
                    <div className="py-2">
                        {listLoading && (
                            <div className="flex items-center justify-center py-6 text-zinc-500">
                                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading history…
                            </div>
                        )}
                        {!listLoading && list.length === 0 && (
                            <p className="text-sm text-zinc-500 italic py-6 text-center">
                                No settles recorded yet for this cashier.
                            </p>
                        )}
                        {!listLoading && list.length > 0 && (
                            <div className="rounded border border-zinc-800 divide-y divide-zinc-800 max-h-96 overflow-y-auto"
                                data-testid="payout-history-list">
                                {list.map((p, i) => (
                                    <button key={p.id} type="button"
                                        onClick={() => openTrail(p.id)}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-left hover:bg-zinc-900/60 transition-colors focus:outline-none focus:bg-zinc-900"
                                        data-testid={`payout-history-row-${i}`}>
                                        <span className="font-mono font-semibold text-amber-300 w-20">
                                            {fmt(p.amount)}
                                        </span>
                                        <span className="text-zinc-400 flex-1">
                                            {new Date(p.created_at).toLocaleString()}
                                        </span>
                                        <span className="text-[10px] uppercase text-zinc-500">
                                            {p.initiated_by_role === 'cashier' ? 'self-reported' : 'you marked paid'}
                                        </span>
                                        {p.note && (
                                            <span className="text-zinc-500 italic truncate max-w-[200px]" title={p.note}>
                                                {p.note}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {trail && (
                    <div className="space-y-3 py-2" data-testid="payout-trail-panel">
                        <div className="flex items-baseline gap-2 pb-2 border-b border-zinc-800">
                            <span className="text-amber-300 font-mono text-lg font-bold">
                                {fmt(trail.payout.amount)}
                            </span>
                            <span className="text-xs text-zinc-400">
                                {new Date(trail.payout.created_at).toLocaleString()} ·{' '}
                                {trail.payout.initiated_by_role === 'cashier' ? 'self-reported' : 'marked paid'}
                            </span>
                            <Button size="sm" variant="outline" onClick={() => setTrail(null)}
                                className="ml-auto h-7 text-xs border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                                data-testid="trail-back-btn">
                                ← Back
                            </Button>
                        </div>

                        {trailLoading && (
                            <div className="flex items-center justify-center py-6 text-zinc-500">
                                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading trail…
                            </div>
                        )}

                        <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="rounded border border-zinc-800 bg-zinc-950/50 px-3 py-2">
                                <p className="text-[10px] uppercase text-zinc-500">Loaded</p>
                                <p className="font-mono text-emerald-300 font-semibold">{fmt(trail.totals.loaded)}</p>
                            </div>
                            <div className="rounded border border-zinc-800 bg-zinc-950/50 px-3 py-2">
                                <p className="text-[10px] uppercase text-zinc-500">Cashed</p>
                                <p className="font-mono text-rose-300 font-semibold">{fmt(trail.totals.cashed)}</p>
                            </div>
                            <div className="rounded border border-zinc-800 bg-zinc-950/50 px-3 py-2">
                                <p className="text-[10px] uppercase text-zinc-500">Net</p>
                                <p className="font-mono text-amber-300 font-semibold">{fmt(trail.totals.net)}</p>
                            </div>
                        </div>

                        <p className="text-[11px] text-zinc-500">
                            {trail.window?.from
                                ? <>Window: {new Date(trail.window.from).toLocaleString()} → {new Date(trail.window.to).toLocaleString()}</>
                                : <>Window: all activity up to {new Date(trail.window.to).toLocaleString()}</>}
                            {' · '}{trail.totals.txn_count} txns
                        </p>

                        {trail.transactions.length === 0 ? (
                            <p className="text-sm text-zinc-500 italic py-4 text-center">
                                No customer activity in this settle window.
                            </p>
                        ) : (
                            <div className="rounded border border-zinc-800 divide-y divide-zinc-800 max-h-80 overflow-y-auto"
                                data-testid="trail-txns">
                                {trail.transactions.map(tx => {
                                    const isLoad = tx.type === 'cashier_load';
                                    return (
                                        <div key={tx.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                                            {isLoad
                                                ? <ArrowDownCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                                                : <ArrowUpCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-zinc-200 truncate">
                                                    {tx.player_name || 'Unknown'}
                                                    {tx.player_phone && (
                                                        <span className="text-zinc-500 ml-1">· {tx.player_phone}</span>
                                                    )}
                                                </p>
                                                {tx.note && (
                                                    <p className="text-[10px] text-zinc-500 truncate">{tx.note}</p>
                                                )}
                                            </div>
                                            <span className={`font-mono font-semibold ${isLoad ? 'text-emerald-300' : 'text-rose-300'}`}>
                                                {isLoad ? '+' : '−'}{fmt(tx.amount)}
                                            </span>
                                            <span className="text-[10px] text-zinc-500 shrink-0 w-20 text-right">
                                                {new Date(tx.created_at).toLocaleString(undefined, { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}
                        className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800"
                        data-testid="payout-history-close">
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
